import logger from '../utils/logger';
import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { GlobalStateContext } from "../utils/GlobalStateContext";
import { UserContext } from './UserContext';
import { InputMask } from '@react-input/mask';
import '../style/PatientAbout.css';
import UserDetails from './customComponents/tracker';
import { convertUTCToLocal, dateOfBirth, formatTo12Hour, getUserId, formatAppointmentTimeRange } from '../utils/utils';
import PageTopBar from './customComponents/PageTopBar';
import { Home, FileText, ShieldCheck, Save, X, ClipboardPlus } from 'lucide-react';
import CreateEmrRecordModal from './customComponents/CreateEmrRecordModal';
import ProfilePictureCard from './customComponents/ProfilePictureCard';
import InsuranceCardScanner from './customComponents/InsuranceCardScanner';
import LocationModal from './customComponents/LocationModal';
import { getLocationStageColor } from '../utils/utils';

// Main component
const PatientAbout = ({ patientId, userId}) => {
  // Initial demographics data (fetched or from props in a real app)
  const BASE_URL = process.env.REACT_APP_API_URL;
  const { currentPatient, setCurrentPatient } = useContext(GlobalStateContext);
  const {currentUser} = useContext(GlobalStateContext);
  const user = getUserId(currentUser);
  const navigate = useNavigate();

  //demographics is the one that we are changing
  const [demographics, setDemographics] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    dob: '',
    sex: '',
    ssn: '',
    street_number: '',
    street_name: '',
    city: '',
    state: '',
    zip: '',
    primary_phone: '',
    cell_phone: '',
    email: '',
    guardian_name: '',
  });
  //keeping a record temp to compare to
  const [tempDemographics, setTempDemographics] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    dob: '',
    sex: '',
    ssn: '',
    street_number: '',
    street_name: '',
    city: '',
    state: '',
    zip: '',
    primary_phone: '',
    cell_phone: '',
    email: '',
    guardian_name: '',
  });
  // keeping track of our upcoming appointments
  const [upcomingAppointments, setUpcomingAppointments] = useState({appointments: []});
  const [emergencyContacts, setEmergencyContacts] = useState({contacts: []});
  const [billingInfo, setBillingInfo] = useState({insurances: []});

  //const [demographics, setDemographics] = useState(initialDemographics);
  
  useEffect(() => {
    // logger.log(currentPatient)
  if(currentPatient && currentPatient.patient !== null){
    const getDemographics = async () => {
      if (currentPatient.ris_patients_id) {
        try {
          const response = await fetch(`${BASE_URL}/patients/aboutPatient?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`);
          if (response.status === 200) {
            const data = await response.json();
            setDemographics(data[0]); // setDemographics(prev => (...prev, data[0]));
            setTempDemographics(data[0]);
          } else {
            // logger.log(response);
            setDemographics(initialDemographics);
          }
        } catch (error) {
          logger.error("Error fetching appointments", error);
        }
    }
    else {
      try {
        const response = await fetch(`${BASE_URL}/patients/aboutPatient?new_patients_id=${currentPatient.new_patients_id}&user=${user}&type=${currentUser.type}`);
        if (response.status === 200) {
          const data = await response.json();
          // logger.log(data[0]);
          setDemographics(data[0]);
        } else {
          // logger.log(response);
          setDemographics(initialDemographics);
        }
      } catch (error) {
        logger.error("Error fetching appointments", error);
      }
    }
  } 

  const getUpcomingAppointments = async () => {
    try {
        const response = await fetch(`${BASE_URL}/patients/aboutPatientAppointments?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`);
        if (response.ok) {
          const data = await response.json();
          // logger.log(data);
          if (data.length > 0) {
          setUpcomingAppointments({appointments: data});
          }
        } else {
          // logger.log("getUpcomingAppointments failed");
          setUpcomingAppointments(upcomingAppointments);
         
        }
      } catch (error) {
        logger.error("Error fetching appointments", error);
      }
  };

  const getEmergencyContacts = async () => {
    try {
        const response = await fetch(`${BASE_URL}/patients/aboutPatientContacts?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`);
        if (response.ok) {
          const data = await response.json();
          // logger.log(data);
          if (data.length > 0) {
          setEmergencyContacts({contacts: data});
          }
        } else {
          // logger.log("getUpcomingAppointments failed");
          setEmergencyContacts(emergencyContacts);

        }
      } catch (error) {
        logger.error("Error fetching appointments", error);
      }
  };

  const getBillingInfo = async () => {
    try {
        const response = await fetch(`${BASE_URL}/patients/aboutPatientBilling?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`);
        if (response.ok) {
          const data = await response.json();
          logger.log(`Fetched ${data.length} insurance records:`, data);
          if (data.length > 0) {
          setBillingInfo({insurances: data});
          } else {
            setBillingInfo({insurances: []});
          }
        } else {
          logger.log("getBillingInfo failed with status:", response.status);
          setBillingInfo({insurances: []});
        }
      } catch (error) {
        logger.error("Error fetching billing information", error);
        setBillingInfo({insurances: []});
      }
  };


  const getIdentityStatus = async () => {
    if (!currentPatient.ris_patients_id) return;
    try {
      const response = await fetch(`${BASE_URL}/patientIdentity/status?ris_patient_id=${currentPatient.ris_patients_id}`);
      if (response.ok) {
        const data = await response.json();
        setIdentityStatus(data);
      }
    } catch (error) {
      logger.error('Error fetching patient identity status:', error);
    }
  };

  const getCurrentLocation = async () => {
    if (!currentPatient.ris_patients_id) return;
    try {
      const response = await fetch(`${BASE_URL}/patients/currentLocation?ris_patients_id=${currentPatient.ris_patients_id}`);
      if (response.ok) {
        const data = await response.json();
        setCurrentLocation(data);
      }
    } catch (error) {
      logger.error('Error fetching current location:', error);
    }
  };

  getDemographics();
  getUpcomingAppointments();
  getEmergencyContacts();
  getBillingInfo();
  getIdentityStatus();
  getCurrentLocation();
} else {
  navigate('/home');
}

}, [currentPatient]);


  // Patient identity / EMR link status
  const [identityStatus, setIdentityStatus] = useState({ hasIdentity: false, emrLinked: false, consentGiven: false, mrn: null });
  const [showCreateEmrModal, setShowCreateEmrModal] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [showLocationModal, setShowLocationModal] = useState(false);

  // Track changes separately to allow discarding if not saved
  const [isDirty, setIsDirty] = useState(false);

  // Insurance modal state
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [showAddInsuranceForm, setShowAddInsuranceForm] = useState(false);
  const [addInsuranceLoading, setAddInsuranceLoading] = useState(false);
  // Scanned card images keyed by billing_information id (bi_id), with signed URLs.
  const [insuranceCards, setInsuranceCards] = useState({});
  const [newInsurance, setNewInsurance] = useState({
    insurance_name: '',
    insurance_address: '',
    insurance_city: '',
    insurance_state: '',
    insurance_zip: '',
    insurance_phone: '',
    member_id: '',
    group_number: '',
    effective_date: '',
    termination_date: '',
    subscriber_name: '',
    subscriber_dob: '',
    relationship: '',
  });
  // GCS object paths of card images scanned for the in-progress new insurance plan,
  // persisted (insurance_card_images) when the plan is saved.
  const [newCardImages, setNewCardImages] = useState([]);

  const resetNewInsurance = () => {
    setNewInsurance({
      insurance_name: '', insurance_address: '', insurance_city: '',
      insurance_state: '', insurance_zip: '', insurance_phone: '',
      member_id: '', group_number: '', effective_date: '',
      termination_date: '', subscriber_name: '', subscriber_dob: '', relationship: '',
    });
    setNewCardImages([]);
  };

  const handleNewInsuranceChange = (e) => {
    const { name, value } = e.target;
    setNewInsurance(prev => ({ ...prev, [name]: value }));
  };

  // Pre-fill the form from OCR-extracted card fields (only fields with non-zero
  // confidence overwrite existing values, so manual edits aren't clobbered).
  const handleScannerFields = (uid, fields, fieldDetails) => {
    setNewInsurance(prev => {
      const updated = { ...prev };
      for (const [key, value] of Object.entries(fields || {})) {
        if (value && fieldDetails?.[key]?.confidence > 0 && key in updated) {
          updated[key] = value;
        }
      }
      return updated;
    });
  };

  // Capture the uploaded card image paths so they're saved with the new plan.
  const handleScannerImages = (uid, images) => {
    if (!images || !images.length) return;
    setNewCardImages(prev => [...prev, ...images]);
  };

  const handleAddInsuranceSubmit = async () => {
    if (!newInsurance.insurance_name || !newInsurance.member_id || !newInsurance.subscriber_name || !newInsurance.relationship) {
      alert('Please fill in all required fields: Insurance Name, Member ID, Subscriber Name, and Relationship.');
      return;
    }
    setAddInsuranceLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/patients/aboutPatientBilling?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...newInsurance, card_images: newCardImages }),
        }
      );
      if (response.ok) {
        // Refresh billing info
        const refreshRes = await fetch(`${BASE_URL}/patients/aboutPatientBilling?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`);
        if (refreshRes.ok) {
          const data = await refreshRes.json();
          setBillingInfo({ insurances: data });
        }
        // Reload signed card-image URLs so the just-scanned card shows on the policy.
        await getInsuranceCards();
        resetNewInsurance();
        setShowAddInsuranceForm(false);
      } else {
        const errData = await response.json();
        alert(errData.msg || 'Failed to add insurance plan.');
      }
    } catch (error) {
      logger.error('Error adding insurance:', error);
      alert('An error occurred while adding insurance.');
    } finally {
      setAddInsuranceLoading(false);
    }
  };



  // Calculate age to determine if patient is a minor
  const calculateAge = (dob) => {
    if (!dob) return null;
    const [year, month, day] = dob.split('T')[0].split('-').map(Number);
    const today = new Date();
    let age = today.getFullYear() - year;
    const m = (today.getMonth() + 1) - month;
    if (m < 0 || (m === 0 && today.getDate() < day)) {
      age--;
    }
    return age;
  };

  const isMinor = calculateAge(tempDemographics.dob) < 18;

  // Handle input changes // tempDemographics is being updated here, whatever e is referencing
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDemographics( { ...demographics, [name]: value });
    setIsDirty(true);
  };

  // Save changes and log to admin section
  const handleSave = async () => {
    try {
        const queryParam = currentPatient.ris_patients_id
            ? `ris_patients_id=${currentPatient.ris_patients_id}`
            : `new_patients_id=${currentPatient.new_patients_id}`;

                  // Compare original and temporary demographics
        const updatedFields = Object.keys(demographics).reduce((changes, key) => {
          if (tempDemographics[key] !== demographics[key] && demographics[key] !== "") {
            changes[key] = demographics[key]; // Only include changed fields
          }
        return changes;
        }, {});

        if (Object.keys(updatedFields).length === 0) {
            // logger.log("No changes detected, skipping update.");
            return; // Avoid making unnecessary requests
        }
            
        // logger.log(queryParam)
        const response = await fetch(`${BASE_URL}/patients/aboutPatient?${queryParam}&user=${user}&type=${currentUser.type}`, {  //checking this out in backend
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(updatedFields),
        });

        if (response.ok) {  
            const updatedData = await response.json();
            // logger.log(updatedFields)
            // logger.log('Patient updated successfully:', updatedData);
            setDemographics( prev => ({...prev, ...updatedFields})); // Apply updates to states
            // setTempDemographics(prev => ({ ...prev, ...updatedFields }));
            setIsDirty(false);
        } else {
            // logger.log(updatedFields)
            logger.error('Failed to update patient:', await response.json());
        }
    } catch (error) {
        logger.error('Error updating patient:', error);
    }
};

  // Discard changes
  const handleCancel = () => {
    setDemographics({ ...tempDemographics });
    setIsDirty(false);
  };

  // Fetch scanned card images (signed URLs expire in 10 min, so fetch on modal open).
  const getInsuranceCards = async () => {
    if (!currentPatient.ris_patients_id) return;
    try {
      const response = await fetch(`${BASE_URL}/patients/aboutPatientInsuranceCards?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`);
      if (response.ok) {
        const data = await response.json();
        const byBiId = {};
        data.forEach(img => {
          if (!byBiId[img.bi_id]) byBiId[img.bi_id] = [];
          byBiId[img.bi_id].push(img);
        });
        setInsuranceCards(byBiId);
      } else {
        setInsuranceCards({});
      }
    } catch (error) {
      logger.error("Error fetching insurance card images", error);
      setInsuranceCards({});
    }
  };

  // Refresh the billing list (used after archive/reactivate changes).
  const refreshBillingInfo = async () => {
    try {
      const res = await fetch(`${BASE_URL}/patients/aboutPatientBilling?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`);
      if (res.ok) {
        const data = await res.json();
        setBillingInfo({ insurances: data });
      }
    } catch (error) {
      logger.error("Error refreshing billing info", error);
    }
  };

  // Archive (soft-delete) an active insurance record.
  const handleArchiveInsurance = async (id) => {
    if (!window.confirm('Archive this insurance policy? It will be kept on file and hidden from active workflows, and can be reactivated later.')) return;
    try {
      const response = await fetch(
        `${BASE_URL}/patients/aboutPatientBilling/archive?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id }),
        }
      );
      if (response.ok) {
        await refreshBillingInfo();
      } else {
        const errData = await response.json().catch(() => ({}));
        alert(errData.msg || 'Failed to archive insurance.');
      }
    } catch (error) {
      logger.error('Error archiving insurance:', error);
      alert('An error occurred while archiving insurance.');
    }
  };

  // Reactivate (un-archive) a previously removed insurance record.
  const handleReactivateInsurance = async (id) => {
    try {
      const response = await fetch(
        `${BASE_URL}/patients/aboutPatientBilling/reactivate?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id }),
        }
      );
      if (response.ok) {
        await refreshBillingInfo();
      } else {
        const errData = await response.json().catch(() => ({}));
        alert(errData.msg || 'Failed to reactivate insurance.');
      }
    } catch (error) {
      logger.error('Error reactivating insurance:', error);
      alert('An error occurred while reactivating insurance.');
    }
  };

  // Same PUT the Location modal's stepper uses from CheckIn.js.
  const handleLocationStatusChange = async (newLocation) => {
    if (!currentLocation?.schedule_id) return;
    try {
      const response = await fetch(`${BASE_URL}/scheduling/updateStatus`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schedule_id: currentLocation.schedule_id, status: newLocation, user, type: currentUser.type }),
      });
      if (response.ok) {
        setCurrentLocation(prev => ({ ...prev, location: newLocation }));
        setShowLocationModal(false);
      } else {
        logger.error('Failed to update status:', await response.json());
      }
    } catch (error) {
      logger.error('Error updating status:', error);
    }
  };

  const handleResumeRegistration = () => {
    if (!currentPatient.ris_patients_id) return;
    setShowLocationModal(false);
    navigate(`/patientRegistration?ris_patients_id=${currentPatient.ris_patients_id}`, {
      state: { restoreDraft: true },
    });
  };

  // Toggle insurance modal; load fresh signed card-image URLs when opening.
  const handleInsuranceInfoModal = () => {
    const opening = !showInsuranceModal;
    setShowInsuranceModal(opening);
    if (opening) getInsuranceCards();
  };

  // Archived policies are retained for history but shown separately from active ones.
  const activeInsuranceCount = billingInfo.insurances.filter(i => !i.archived).length;
  
  return (
    <div className="ris-about-patient">
      <PageTopBar links={[
        { label: 'Home', to: '/home', icon: Home },
        { label: 'Orders', to: '/orders', icon: FileText },
      ]} />
      <div className="ris-about-content">
        <div className="ris-about-page-header">
          <div>
            <h1 className="ris-about-page-title">About Patient</h1>
          </div>
          <div className="ris-about-page-actions">
            {currentPatient.ris_patients_id && !identityStatus.emrLinked && (
              <div className="ris-emr-btn-wrap">
                <button
                  className="ris-about-action-btn emr"
                  onClick={() => setShowCreateEmrModal(true)}
                  disabled={!identityStatus.consentGiven}
                >
                  <ClipboardPlus size={15} /><span>Create EMR Record</span>
                </button>
                {!identityStatus.consentGiven && (
                  <span className="ris-emr-tooltip">
                    HIPAA release missing — patient must complete registration before an EMR record can be created.
                  </span>
                )}
              </div>
            )}
            {/* The old MRN/ID badge lived here; the profile card now carries all
                three identifiers (MRN/RID/PID), so this would only repeat it. */}
            <button className="ris-about-action-btn" onClick={handleInsuranceInfoModal}>
              <ShieldCheck size={15} /><span>Insurance Info</span>
            </button>
            <button className="ris-about-action-btn" onClick={handleSave} disabled={!isDirty}>
              <Save size={15} /><span>Save</span>
            </button>
            <button className="ris-about-action-btn secondary" onClick={handleCancel} disabled={!isDirty}>
              <X size={15} /><span>Cancel</span>
            </button>
          </div>
        </div>

      <div className="ris-about-grid">

      {/* LEFT COLUMN — profile picture + demographics */}
      <div className="ris-about-col ris-about-col-left">

      <div className="ris-about-section ris-about-profile-section">
        <ProfilePictureCard
          risPatientsId={currentPatient?.ris_patients_id}
          user={user}
          type={currentUser?.type}
        />
        <div className="ris-about-profile-name">
          <h2>{[demographics.first_name, demographics.last_name].filter(Boolean).join(' ') || 'Patient'}</h2>
          {demographics.dob && <span>DOB: {dateOfBirth(demographics.dob)}</span>}
          {/* The three identifiers this patient exists under — MRN (the shared
              patient_identity record), RID (their id in this RIS) and PID (their
              id in the EMR). Mirrors the EMR's snapshot header: having all three
              on screen is what makes a cross-system problem diagnosable without
              digging through two databases. */}
          <div className="ris-about-id-row">
            <span className="ris-about-id-item">
              <strong>MRN:</strong> {identityStatus.mrn ?? 'None'}
            </span>
            <span className="ris-about-id-divider">·</span>
            <span className="ris-about-id-item">
              <strong>RID:</strong> {currentPatient?.ris_patients_id ?? 'None'}
            </span>
            <span className="ris-about-id-divider">·</span>
            <span className="ris-about-id-item">
              <strong>PID:</strong> {identityStatus.emr_patient_id ?? 'None'}
            </span>
            {currentLocation && (
              <>
                <span className="ris-about-id-divider">·</span>
                <button
                  className="ris-about-location-badge"
                  style={{ '--stage-color': getLocationStageColor(currentLocation.location) }}
                  onClick={() => setShowLocationModal(true)}
                  title="View location history / change status"
                >
                  {currentLocation.location}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="ris-about-section">
        <h3>Demographics</h3>
        <div className="ris-about-demographics-grid">
        <div className="ris-about-form-group">
          <label>First Name:</label>
          <input  
            type="text"
            name="first_name"
            value={demographics.first_name}
            onChange={handleInputChange}
          />
        </div>
        <div className="ris-about-form-group">
          <label>Middle Name:</label>
          <input
            type="text"
            name="middle_name"
            value={demographics.middle_name}
            onChange={handleInputChange}
          />
        </div>
        <div className="ris-about-form-group">
          <label>Last Name:</label>
          <input
            type="text"
            name="last_name"
            value={demographics.last_name}
            onChange={handleInputChange}
          />
        </div>
        <div className="ris-about-form-group">
          <label>Date of Birth:</label>
          <input
            type="date"
            name="dob"
            value={demographics.dob}
            onChange={handleInputChange}
          />
        </div>
        <div className="ris-about-form-group">
          <label>Sex:</label>
          <select
            name="sex"
            value={demographics.sex}
            onChange={handleInputChange}
          >
            <option value="">-- Select --</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </div>
        <div className="ris-about-form-group">
          <label>SSN:</label>
          <InputMask
            mask= "XXX-XX-XXXX" 
            replacement={{ X: /\d/ }}
            type="text"
            name="ssn"
            value={demographics.ssn}
            onChange={handleInputChange}
          />
        </div>
        <div className="ris-about-form-group">
          <label>Street Number:</label>
          <input
            type="text"
            name="street_number"
            value={demographics.street_number}
            onChange={handleInputChange}
          />
        </div>
        <div className="ris-about-form-group">
          <label>Street Name:</label>
          <input
            type="text"
            name="street_name"
            value={demographics.street_name}
            onChange={handleInputChange}
          />
        </div>
        <div className="ris-about-form-group">
          <label>City:</label>
          <input
            type="text"
            name="city"
            value={demographics.city}
            onChange={handleInputChange}
          />
        </div>
        <div className="ris-about-form-group">
          <label>State:</label>
          <input
            type="text"
            name="state"
            value={demographics.state}
            onChange={handleInputChange}
          />
        </div>
        <div className="ris-about-form-group">
          <label>Zip Code:</label>
          <InputMask
            mask="XXXXX"
            replacement={{ X: /\d/ }}
            type="text"
            name="zip"
            value={demographics.zip}
            onChange={handleInputChange}
          />
        </div>
        <div className="ris-about-form-group">
          <label>Primary Phone:</label>
          <InputMask
            mask="(XXX) XXX-XXXX"
            replacement={{ X: /\d/ }}
            type="text"
            name="primary_phone"
            value={demographics.primary_phone}
            onChange={handleInputChange}
          />
        </div>
        <div className="ris-about-form-group">
          <label>Cell Phone:</label>
          <InputMask
            mask="(XXX) XXX-XXXX"
            replacement={{ X: /\d/ }}
            type="text"
            name="cell_phone"
            value={demographics.cell_phone}
            onChange={handleInputChange}
          />
        </div>
        <div className="ris-about-form-group span-2">
          <label>Email Address:</label>
          <input
            type="email"
            name="email"
            value={demographics.email}
            onChange={handleInputChange}
          />
        </div>
        {isMinor && (
          <div className="ris-about-form-group span-2">
            <label>Parent or Guardian Name:</label>
            <input
              type="text"
              name="guardian_name"
              value={demographics.guardian_name}
              onChange={handleInputChange}
            />
          </div>
        )}
        </div>{/* /ris-about-demographics-grid */}
      </div>

      </div>{/* /ris-about-col-left */}

      {/* RIGHT COLUMN — emergency contacts + upcoming appointments */}
      <div className="ris-about-col ris-about-col-right">

      <div className="ris-about-section">
        <h3>Emergency Contacts</h3>
        {emergencyContacts.contacts.length ?
        <table className="ris-about-appointments-table">
          <thead>
            <tr>
              <th>Contact Name</th>
              <th>Relation</th>
              <th>Phone Number</th>
            </tr>
          </thead>
          <tbody>
              {emergencyContacts.contacts.map((contact, index) => (
                <tr key={index}>
                  <td>{contact.name}</td>
                  <td>{contact.relation}</td>
                  <td>{contact.phone_number}</td>
                </tr>
              ))}
          </tbody>
        </table>
        : 'No emergency contacts' }
      </div>

      <div className="ris-about-section">
        <h3>Upcoming Appointments</h3>
        {emergencyContacts.contacts.length ? 
        <table className="ris-about-appointments-table">
          <thead>
            <tr>
              <th>Visit Type</th>
              {/* <th>Date</th> */}
              <th>Start/End Time</th>
              {/* <th>End Time</th> */}
              <th>Exam</th>
            </tr>
          </thead>
          <tbody>
              {upcomingAppointments.appointments.map((appt, index) => ( 
                
                <tr key={index}>
                  <td>{appt.visit_type}</td>
                  {/* <td>{dateOfBirth(appt.appointment_start)}</td> */}
                  <td>{formatAppointmentTimeRange(appt.appointment_start, appt.appointment_end)}</td>
                  {/* <td>{convertUTCToLocal(appt.appointment_end)}</td> */}
                  <td>{appt.exam}</td>
                </tr>
              ))} 
          </tbody>
        </table>
        : 'No upcoming appointments' }
      </div>

      <div className="ris-about-section">
        <h3>Insurance</h3>
        {activeInsuranceCount > 0 ? (
          <div className="ris-about-insurance-summary">
            {billingInfo.insurances.filter(i => !i.archived).map((ins, idx) => (
              <div key={ins.id || idx} className="ris-insurance-summary-item">
                <div className="ris-insurance-summary-rank">
                  {idx === 0 ? 'Primary' : idx === 1 ? 'Secondary' : `Plan ${idx + 1}`}
                </div>
                <div className="ris-insurance-summary-name">{ins.insurance_name || 'Unnamed plan'}</div>
                <div className="ris-insurance-summary-meta">
                  <span>Member ID: {ins.member_id || 'N/A'}</span>
                  {ins.relationship && <span className="relationship">· {ins.relationship}</span>}
                </div>
              </div>
            ))}
            <button className="ris-insurance-summary-link" onClick={handleInsuranceInfoModal}>
              View full details &rarr;
            </button>
          </div>
        ) : (
          <div className="ris-about-insurance-empty">
            <p>No insurance on file.</p>
            <button
              className="ris-insurance-summary-link"
              onClick={handleInsuranceInfoModal}
              disabled={!currentPatient.ris_patients_id}
            >
              Add insurance &rarr;
            </button>
          </div>
        )}
      </div>

      </div>{/* /ris-about-col-right */}
      </div>{/* /ris-about-grid */}

      {/* Insurance Info Modal */}
      {showInsuranceModal && (
        <div className="ris-insurance-modal-overlay" onClick={handleInsuranceInfoModal}>
          <div className="ris-insurance-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="ris-insurance-modal-header">
              <h3>Insurance Information {activeInsuranceCount > 0 && `(${activeInsuranceCount} ${activeInsuranceCount === 1 ? 'Policy' : 'Policies'})`}</h3>
              <button className="ris-insurance-modal-close" onClick={handleInsuranceInfoModal}>×</button>
            </div>
            <div className="ris-insurance-modal-body">
              {billingInfo.insurances.length > 0 ? (
                <>
                  {billingInfo.insurances.map((insurance, index) => (
                    <div key={insurance.id || index} className="ris-about-insurance-item" style={insurance.archived ? { opacity: 0.65 } : undefined}>
                      <div className="ris-insurance-header">
                        <h4>Insurance {index + 1}{insurance.insurance_name && `: ${insurance.insurance_name}`}</h4>
                        {insurance.archived ? (
                          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, backgroundColor: '#f3f4f6', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                            Archived{insurance.archived_at ? ` · ${dateOfBirth(insurance.archived_at)}` : ''}
                          </span>
                        ) : null}
                        {insurance.archived ? (
                          <button
                            onClick={() => handleReactivateInsurance(insurance.id)}
                            style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6, border: '1px solid #3b82f6', backgroundColor: '#fff', color: '#3b82f6', cursor: 'pointer' }}
                          >
                            Reactivate
                          </button>
                        ) : (
                          <button
                            onClick={() => handleArchiveInsurance(insurance.id)}
                            style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#6b7280', cursor: 'pointer' }}
                          >
                            Archive
                          </button>
                        )}
                      </div>
                      <div className="ris-about-insurance-details">
                        <div className="ris-about-form-group">
                          <label>Insurance Name:</label>
                          <span>{insurance.insurance_name || 'N/A'}</span>
                        </div>
                        <div className="ris-about-form-group">
                          <label>Insurance Address:</label>
                          <span>
                            {insurance.insurance_address || ''}
                            {insurance.insurance_city && `, ${insurance.insurance_city}`}
                            {insurance.insurance_state && `, ${insurance.insurance_state}`}
                            {insurance.insurance_zip && ` ${insurance.insurance_zip}`}
                            {!insurance.insurance_address && !insurance.insurance_city && !insurance.insurance_state && !insurance.insurance_zip && 'N/A'}
                          </span>
                        </div>
                        <div className="ris-about-form-group">
                          <label>Insurance Phone:</label>
                          <span>{insurance.insurance_phone || 'N/A'}</span>
                        </div>
                        <div className="ris-about-form-group">
                          <label>Member ID:</label>
                          <span>{insurance.member_id || 'N/A'}</span>
                        </div>
                        <div className="ris-about-form-group">
                          <label>Group Number:</label>
                          <span>{insurance.group_number || 'N/A'}</span>
                        </div>
                        <div className="ris-about-form-group">
                          <label>Effective Date:</label>
                          <span>{insurance.effective_date ? dateOfBirth(insurance.effective_date) : 'N/A'}</span>
                        </div>
                        <div className="ris-about-form-group">
                          <label>Termination Date:</label>
                          <span>{insurance.termination_date ? dateOfBirth(insurance.termination_date) : 'N/A'}</span>
                        </div>
                        <div className="ris-about-form-group">
                          <label>Subscriber Name:</label>
                          <span>{insurance.subscriber_name || 'N/A'}</span>
                        </div>
                        <div className="ris-about-form-group">
                          <label>Subscriber DOB:</label>
                          <span>{insurance.subscriber_dob ? dateOfBirth(insurance.subscriber_dob) : 'N/A'}</span>
                        </div>
                        <div className="ris-about-form-group">
                          <label>Relationship:</label>
                          <span>{insurance.relationship ? insurance.relationship.toUpperCase() : 'N/A'}</span>
                        </div>
                      </div>

                      {/* Scanned card images for this policy (click to open full size) */}
                      {insuranceCards[insurance.id]?.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <label style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>Scanned Card Images:</label>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 6 }}>
                            {insuranceCards[insurance.id].map(img => (
                              img.url ? (
                                <a key={img.id} href={img.url} target="_blank" rel="noopener noreferrer" style={{ textAlign: 'center', textDecoration: 'none' }}>
                                  <img src={img.url} alt={`Insurance card ${img.side || ''}`} style={{ width: 150, height: 94, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb', display: 'block' }} />
                                  <span style={{ fontSize: 11, color: '#6b7280', textTransform: 'capitalize' }}>{img.side || 'card'}</span>
                                </a>
                              ) : (
                                <div key={img.id} style={{ width: 150, height: 94, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, border: '1px dashed #d1d5db', color: '#9ca3af', fontSize: 11 }}>
                                  Image unavailable
                                </div>
                              )
                            ))}
                          </div>
                        </div>
                      )}

                      {index < billingInfo.insurances.length - 1 && <hr className="ris-insurance-divider" />}
                    </div>
                  ))}
                </>
              ) : (
                <p className="ris-no-insurance-message">No insurance information on file.</p>
              )}

              {/* Add Insurance Plan Section */}
              {!showAddInsuranceForm ? (
                <button
                  className="ris-add-insurance-btn"
                  onClick={() => setShowAddInsuranceForm(true)}
                  disabled={!currentPatient.ris_patients_id}
                >
                  + Add Insurance Plan
                </button>
              ) : (
                <div className="ris-add-insurance-form">
                  <div className="ris-insurance-header">
                    <h4>New Insurance Plan</h4>
                  </div>
                  <div className="ris-add-insurance-form-body">
                    <div className="ris-add-insurance-row">
                      <div className="ris-add-insurance-field">
                        <label>Insurance Name <span className="ris-required">*</span></label>
                        <input type="text" name="insurance_name" value={newInsurance.insurance_name} onChange={handleNewInsuranceChange} placeholder="e.g. Blue Cross Blue Shield" />
                      </div>
                      <div className="ris-add-insurance-field">
                        <label>Insurance Phone</label>
                        <InputMask mask="(XXX) XXX-XXXX" replacement={{ X: /\d/ }} type="text" name="insurance_phone" value={newInsurance.insurance_phone} onChange={handleNewInsuranceChange} placeholder="(000) 000-0000" />
                      </div>
                    </div>
                    <div className="ris-add-insurance-row">
                      <div className="ris-add-insurance-field">
                        <label>Street Address</label>
                        <input type="text" name="insurance_address" value={newInsurance.insurance_address} onChange={handleNewInsuranceChange} placeholder="123 Main St" />
                      </div>
                    </div>
                    <div className="ris-add-insurance-row ris-add-insurance-row-3">
                      <div className="ris-add-insurance-field">
                        <label>City</label>
                        <input type="text" name="insurance_city" value={newInsurance.insurance_city} onChange={handleNewInsuranceChange} placeholder="City" />
                      </div>
                      <div className="ris-add-insurance-field">
                        <label>State</label>
                        <input type="text" name="insurance_state" value={newInsurance.insurance_state} onChange={handleNewInsuranceChange} placeholder="State" />
                      </div>
                      <div className="ris-add-insurance-field">
                        <label>Zip</label>
                        <InputMask mask="XXXXX" replacement={{ X: /\d/ }} type="text" name="insurance_zip" value={newInsurance.insurance_zip} onChange={handleNewInsuranceChange} placeholder="00000" />
                      </div>
                    </div>
                    <hr className="ris-insurance-form-divider" />
                    <div className="ris-add-insurance-row">
                      <div className="ris-add-insurance-field">
                        <label>Member ID <span className="ris-required">*</span></label>
                        <input type="text" name="member_id" value={newInsurance.member_id} onChange={handleNewInsuranceChange} placeholder="Member ID" />
                      </div>
                      <div className="ris-add-insurance-field">
                        <label>Group Number</label>
                        <input type="text" name="group_number" value={newInsurance.group_number} onChange={handleNewInsuranceChange} placeholder="Group Number" />
                      </div>
                    </div>
                    <div className="ris-add-insurance-row">
                      <div className="ris-add-insurance-field">
                        <label>Effective Date</label>
                        <input type="date" name="effective_date" value={newInsurance.effective_date} onChange={handleNewInsuranceChange} />
                      </div>
                      <div className="ris-add-insurance-field">
                        <label>Termination Date</label>
                        <input type="date" name="termination_date" value={newInsurance.termination_date} onChange={handleNewInsuranceChange} />
                      </div>
                    </div>
                    <hr className="ris-insurance-form-divider" />
                    <div className="ris-add-insurance-row ris-add-insurance-row-3">
                      <div className="ris-add-insurance-field">
                        <label>Subscriber Name <span className="ris-required">*</span></label>
                        <input type="text" name="subscriber_name" value={newInsurance.subscriber_name} onChange={handleNewInsuranceChange} placeholder="Full Name" />
                      </div>
                      <div className="ris-add-insurance-field">
                        <label>Subscriber DOB</label>
                        <input type="date" name="subscriber_dob" value={newInsurance.subscriber_dob} onChange={handleNewInsuranceChange} />
                      </div>
                      <div className="ris-add-insurance-field">
                        <label>Relationship <span className="ris-required">*</span></label>
                        <select name="relationship" value={newInsurance.relationship} onChange={handleNewInsuranceChange}>
                          <option value="">-- Select --</option>
                          <option value="self">Self</option>
                          <option value="parent">Parent</option>
                          <option value="guardian">Guardian</option>
                          <option value="spouse">Spouse</option>
                          <option value="child">Child</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    {/* Scan an insurance card to auto-fill the fields above and
                        attach the card images to this policy. */}
                    <InsuranceCardScanner
                      index="new"
                      uid="new"
                      risPatientId={currentPatient.ris_patients_id}
                      onFieldsExtracted={handleScannerFields}
                      onImagesUploaded={handleScannerImages}
                    />

                    <div className="ris-add-insurance-actions">
                      <button
                        className="ris-add-insurance-submit"
                        onClick={handleAddInsuranceSubmit}
                        disabled={addInsuranceLoading}
                      >
                        {addInsuranceLoading ? 'Saving...' : 'Save Insurance Plan'}
                      </button>
                      <button
                        className="ris-add-insurance-cancel"
                        onClick={() => { resetNewInsurance(); setShowAddInsuranceForm(false); }}
                        disabled={addInsuranceLoading}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      </div>

      {showLocationModal && currentLocation && (
        <LocationModal
          appointment={{
            schedule_id: currentLocation.schedule_id,
            ris_patients_id: currentPatient.ris_patients_id,
            patient_name: [demographics.first_name, demographics.last_name].filter(Boolean).join(' '),
          }}
          currentStatus={currentLocation.location}
          onSelectStatus={handleLocationStatusChange}
          onResumeRegistration={handleResumeRegistration}
          onCancel={() => setShowLocationModal(false)}
        />
      )}

      <CreateEmrRecordModal
        isOpen={showCreateEmrModal}
        onClose={() => setShowCreateEmrModal(false)}
        onSuccess={(mrn, emrPatientId) => setIdentityStatus(prev => ({
          ...prev,
          emrLinked: true,
          mrn,
          // Carried through so the PID on the profile card fills in immediately
          // rather than staying "None" until the page is reloaded.
          emr_patient_id: emrPatientId,
        }))}
        risPatientId={currentPatient.ris_patients_id}
        demographics={demographics}
        user={user}
        type={currentUser?.type}
      />
    </div>
  );
};

export default PatientAbout;